import React, { StrictMode, useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ClerkProvider } from '@clerk/clerk-react'
import App from './App'
import ErrorFallback from './pages/ErrorFallback'
import { envConfig } from './config/env'
import '../app/globals.css'

const publishableKey = envConfig.clerk.publishableKey

if (!publishableKey) {
  throw new Error('Missing Clerk Publishable Key - set VITE_CLERK_PUBLISHABLE_KEY or NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY in your environment')
}

// Validate Clerk publishable key format
if (!publishableKey.startsWith('pk_')) {
  console.error('Invalid Clerk Publishable Key format. Expected to start with "pk_"')
  console.error('Key received:', publishableKey)
  console.error('Get the correct key at: https://dashboard.clerk.com/last-active?path=api-keys')
}

// Error Boundary Component
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error?: string}> {
  constructor(props: {children: React.ReactNode}) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    console.error('Error Boundary caught:', error)
    return { hasError: true, error: error.message }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error Details:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />
    }
    return this.props.children
  }
}

// Component wrapper to handle Clerk errors gracefully
function AppWithErrorBoundary() {
  const [hasError, setHasError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    // Global error handler
    const handleError = (event: ErrorEvent) => {
      console.error('Global Error:', event.message, event.error)
      
      if (event.message.includes('Clerk') || event.message.includes('publishableKey')) {
        console.error('Clerk Error detected')
        setErrorMsg(event.message)
        setHasError(true)
      } else if (event.message.includes('React')) {
        console.error('React Error detected')
        setErrorMsg('React initialization failed')
        setHasError(true)
      }
    }

    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled Promise Rejection:', event.reason)
      if (String(event.reason).includes('Clerk')) {
        setErrorMsg('Clerk initialization failed')
        setHasError(true)
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)
    
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])

  if (hasError) {
    return <ErrorFallback />
  }

  try {
    return (
      <ClerkProvider 
        publishableKey={publishableKey}
        signInUrl={envConfig.clerk.signInUrl}
        signUpUrl={envConfig.clerk.signUpUrl}
        afterSignInUrl={envConfig.clerk.afterSignInUrl}
        afterSignUpUrl={envConfig.clerk.afterSignUpUrl}
      >
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ClerkProvider>
    )
  } catch (error) {
    console.error('Render error:', error)
    setHasError(true)
    return <ErrorFallback />
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) {
  document.body.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100vh;background:#000;color:#fff;font-family:monospace"><div><h1>Error: Missing root element</h1><p>div#root not found in index.html</p></div></div>'
} else {
  createRoot(rootElement).render(
    <StrictMode>
      <ErrorBoundary>
        <AppWithErrorBoundary />
      </ErrorBoundary>
    </StrictMode>,
  )
}
